import React, { useState, useEffect, useCallback, useRef } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { auth } from './components/firebaseConfig';
import { onAuthStateChanged, signOut } from 'firebase/auth';
import { Box, CircularProgress, Typography } from '@mui/material';
import Login from './components/Login';
import Home from './components/Home';
import ProfitLossChart from './components/ProfitLossChart';
import Header from './components/Header';
import FileUpload from './components/FileUpload';
import TaxSummary from './components/TaxSummary';
import PayrollJournal from './components/PayrollJournal';
import PersonalFinance from './components/PersonalFinance';
import Settings from './components/Settings';
import PrivacyPolicy from './components/PrivacyPolicy';
import BusinessUseOfHome from './components/BusinessUseOfHome';
import PersonalExpenses from './components/PersonalExpenses';
import PlaidAccounts from './components/PlaidAccounts';
import PlaidAccountTransactions from './components/PlaidAccountTransactions';
import NotFound from './components/NotFound';

const INACTIVITY_TIMEOUT = 30 * 60 * 1000; // 30 minutes

function App() {
    const [user, setUser] = useState(null);
    const [mfaVerified, setMfaVerified] = useState(false);
    const [authLoading, setAuthLoading] = useState(true);
    const inactivityTimer = useRef(null);

    const handleLogout = useCallback(() => {
        sessionStorage.removeItem('mfaVerified');
        signOut(auth).then(() => {
            setUser(null);
            setMfaVerified(false);
        }).catch(console.error);
    }, []);

    const resetInactivityTimer = useCallback(() => {
        if (inactivityTimer.current) clearTimeout(inactivityTimer.current);
        inactivityTimer.current = setTimeout(() => {
            handleLogout();
        }, INACTIVITY_TIMEOUT);
    }, [handleLogout]);

    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
            setUser(currentUser);
            if (currentUser) {
                const sessionFlag = sessionStorage.getItem('mfaVerified');
                setMfaVerified(sessionFlag === 'true');
            } else {
                setMfaVerified(false);
            }
            setAuthLoading(false);
        });
        return () => unsubscribe();
    }, []);

    useEffect(() => {
        if (!user || !mfaVerified) return;

        resetInactivityTimer();
        const events = ['mousedown', 'keydown', 'scroll', 'touchstart'];
        events.forEach(e => window.addEventListener(e, resetInactivityTimer));

        return () => {
            events.forEach(e => window.removeEventListener(e, resetInactivityTimer));
            if (inactivityTimer.current) clearTimeout(inactivityTimer.current);
        };
    }, [user, mfaVerified, resetInactivityTimer]);

    if (authLoading) {
        return (
            <Box sx={{
                minHeight: '100vh', display: 'flex', flexDirection: 'column',
                alignItems: 'center', justifyContent: 'center',
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
            }}>
                <CircularProgress sx={{ color: 'white', mb: 2 }} />
                <Typography sx={{ color: 'white', opacity: 0.8 }}>Loading...</Typography>
            </Box>
        );
    }

    const isAuthenticated = user && mfaVerified;

    return (
        <Router>
            {isAuthenticated && <Header />}
            <Routes>
                <Route path="/login" element={
                    isAuthenticated ? <Navigate to="/" /> : <Login onMfaComplete={() => setMfaVerified(true)} />
                } />
                <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                <Route
                    path="/"
                    element={isAuthenticated ? <Home /> : <Navigate to="/login" />}
                />
                <Route 
                    path="/profit-loss" 
                    element={isAuthenticated ? <ProfitLossChart /> : <Navigate to="/login" />} 
                />
                <Route 
                    path="/upload" 
                    element={isAuthenticated ? <FileUpload /> : <Navigate to="/login" />} 
                />
                <Route 
                    path="/tax-summary" 
                    element={isAuthenticated ? <TaxSummary /> : <Navigate to="/login" />} 
                />
                <Route 
                    path="/payroll" 
                    element={isAuthenticated ? <PayrollJournal /> : <Navigate to="/login" />} 
                />
                <Route 
                    path="/personal-finance" 
                    element={isAuthenticated ? <PersonalFinance /> : <Navigate to="/login" />} 
                />
                <Route 
                    path="/personal-expenses" 
                    element={isAuthenticated ? <PersonalExpenses /> : <Navigate to="/login" />} 
                />
                <Route 
                    path="/linked-accounts" 
                    element={isAuthenticated ? <PlaidAccounts /> : <Navigate to="/login" />} 
                />
                <Route 
                    path="/account-transactions" 
                    element={isAuthenticated ? <PlaidAccountTransactions /> : <Navigate to="/login" />} 
                />
                <Route 
                    path="/home-office" 
                    element={isAuthenticated ? <BusinessUseOfHome /> : <Navigate to="/login" />} 
                />
                <Route 
                    path="/settings" 
                    element={isAuthenticated ? <Settings /> : <Navigate to="/login" />} 
                />
                <Route 
                    path="/security" 
                    element={isAuthenticated ? <Navigate to="/settings?tab=security" replace /> : <Navigate to="/login" />} 
                />
                <Route path="*" element={isAuthenticated ? <NotFound /> : <Navigate to="/login" replace />} />
            </Routes>
        </Router>
    );
}

export default App;
